/**
 * Generic ranking utility used by surfaces that need to display a list of
 * items with recommended/personalized items appearing first.
 *
 * Walks `recommendedIds` first (preserving rank), then appends remaining
 * `items` in their original order. Items whose `idOf` returns nullish
 * are dropped.
 */
export type RankByRecommendationsArgs<T> = {
	items: T[];
	recommendedIds: readonly string[];
	idOf: (item: T) => string | null | undefined;
};

export const rankByRecommendations = <T>({
	items,
	recommendedIds,
	idOf,
}: RankByRecommendationsArgs<T>): T[] => {
	const byId = new Map<string, T>();
	for (const item of items) {
		const id = idOf(item);
		if (id && !byId.has(id)) {
			byId.set(id, item);
		}
	}

	const consumed = new Set<string>();
	const ranked: T[] = [];

	for (const id of recommendedIds) {
		if (consumed.has(id)) {
			continue;
		}
		const item = byId.get(id);
		if (item) {
			ranked.push(item);
			consumed.add(id);
		}
	}

	const remaining = Array.from(byId.entries())
		.filter(([id]) => !consumed.has(id))
		.map(([, item]) => item);

	return ranked.concat(remaining);
};
